import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  LinearProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Progress update type from SSE
interface ProgressUpdate {
  operation: string;
  current: number;
  total: number;
  message: string;
  percent: number;
  timestamp: string;
  metadata?: {
    found?: number;
    errors?: number;
    [key: string]: any;
  };
}

// Feature test result
interface FeatureTestResult {
  feature: string;
  status: 'running' | 'success' | 'error' | 'idle';
  tested: number;
  total: number;
  passed: number;
  failed: number;
  duration?: number;
  error?: string;
  results?: any[];
}

const Backtest: React.FC = () => {
  // State
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [featureTests, setFeatureTests] = useState<Map<string, FeatureTestResult>>(new Map());
  const [numStocks, setNumStocks] = useState<number>(10);
  const [minConfidence, setMinConfidence] = useState<number>(0.6);
  const [error, setError] = useState<string | null>(null);

  // SSE connection ref
  const eventSourceRef = useRef<EventSource | null>(null);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Connect to SSE endpoint for progress updates
  const connectToProgress = (taskId: string) => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(
      `http://localhost:8000/api/v1/recommendations/progress/${taskId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const update: ProgressUpdate = JSON.parse(event.data);
        setProgress(update);

        // Close connection when complete
        if (update.percent >= 100 || update.metadata?.failed) {
          setTimeout(() => {
            eventSource.close();
            setActiveTaskId(null);
          }, 2000);
        }
      } catch (err) {
        console.error('Error parsing SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE connection error:', err);
      eventSource.close();
      setActiveTaskId(null);
      setError('Progress tracking connection lost');
    };

    eventSourceRef.current = eventSource;
  };

  // Run stock scanning test
  const runScanTest = async () => {
    setError(null);
    setProgress(null);

    try {
      // Generate task ID
      const taskId = `scan_${Date.now()}`;
      setActiveTaskId(taskId);

      // Connect to SSE for progress
      connectToProgress(taskId);

      // Trigger scan
      const response = await fetch(
        `http://localhost:8000/api/v1/recommendations/scan-all?` +
        `limit=${numStocks}&min_confidence=${minConfidence}&use_scanner=false&task_id=${taskId}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error(`Scan failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Scan complete:', data);

      // Update feature test results
      setFeatureTests(prev => {
        const updated = new Map(prev);
        updated.set('scan', {
          feature: 'Stock Scanning',
          status: 'success',
          tested: data.total_scanned || 0,
          total: numStocks,
          passed: data.opportunities_found || 0,
          failed: 0,
        });
        return updated;
      });
    } catch (err: any) {
      console.error('Scan test error:', err);
      setError(err.message || 'Scan test failed');
      setActiveTaskId(null);
    }
  };

  // Run feature test (placeholder for individual features)
  const runFeatureTest = async (featureName: string, apiEndpoint: string) => {
    setError(null);

    // Update status to running
    setFeatureTests(prev => {
      const updated = new Map(prev);
      updated.set(featureName, {
        feature: featureName,
        status: 'running',
        tested: 0,
        total: numStocks,
        passed: 0,
        failed: 0,
      });
      return updated;
    });

    try {
      // Simulate feature test (replace with actual API calls)
      const testSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA'].slice(0, numStocks);
      let passed = 0;
      let failed = 0;

      for (let i = 0; i < testSymbols.length; i++) {
        const symbol = testSymbols[i];

        // Update progress
        setFeatureTests(prev => {
          const updated = new Map(prev);
          const current = updated.get(featureName)!;
          updated.set(featureName, {
            ...current,
            tested: i + 1,
          });
          return updated;
        });

        try {
          // Make API call based on feature
          const response = await fetch(
            `http://localhost:8000${apiEndpoint}?symbol=${symbol}`
          );

          if (response.ok) {
            passed++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }

        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Mark as complete
      setFeatureTests(prev => {
        const updated = new Map(prev);
        updated.set(featureName, {
          feature: featureName,
          status: 'success',
          tested: testSymbols.length,
          total: numStocks,
          passed,
          failed,
        });
        return updated;
      });
    } catch (err: any) {
      setFeatureTests(prev => {
        const updated = new Map(prev);
        const current = updated.get(featureName);
        updated.set(featureName, {
          ...current!,
          status: 'error',
          error: err.message,
        });
        return updated;
      });
    }
  };

  // Stop active test
  const stopTest = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setActiveTaskId(null);
    setProgress(null);
  };

  // Feature test cards
  const features = [
    {
      name: 'Stock Scoring',
      key: 'scoring',
      endpoint: '/api/v1/scoring/attractiveness',
      description: 'Tests stock attractiveness scoring with technical indicators',
    },
    {
      name: 'Volatility Ranges',
      key: 'ranges',
      endpoint: '/api/v1/ranges/calculate',
      description: 'Tests Bollinger Bands, ATR, and statistical range predictions',
    },
    {
      name: 'Trading Signals',
      key: 'signals',
      endpoint: '/api/v1/signals',
      description: 'Tests buy/sell signal generation from technical indicators',
    },
    {
      name: 'Market Position',
      key: 'position',
      endpoint: '/api/v1/market-position/analyze',
      description: 'Tests local/global high/low analysis',
    },
    {
      name: 'ML Predictions',
      key: 'ml',
      endpoint: '/api/v1/predictions',
      description: 'Tests XGBoost-based price predictions',
    },
    {
      name: 'Pattern Matching',
      key: 'pattern',
      endpoint: '/api/v1/patterns/similar',
      description: 'Tests historical pattern similarity detection',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Feature Backtest & Validation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test each feature individually or run bulk tests across multiple stocks.
          Progress bars show real-time testing status via Server-Sent Events (SSE).
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Configuration Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Configuration
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Number of Stocks"
                type="number"
                value={numStocks}
                onChange={(e) => setNumStocks(Number(e.target.value))}
                inputProps={{ min: 1, max: 100 }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Min Confidence"
                type="number"
                value={minConfidence}
                onChange={(e) => setMinConfidence(Number(e.target.value))}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={runScanTest}
                  disabled={!!activeTaskId}
                >
                  Run Stock Scan Test
                </Button>
                {activeTaskId && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={stopTest}
                  >
                    Stop
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Live Progress Card */}
      {progress && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Live Progress
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {progress.message}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {progress.percent.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress.percent}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body1">
                  {progress.current} / {progress.total}
                </Typography>
              </Grid>
              {progress.metadata?.found !== undefined && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Opportunities Found
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    {progress.metadata.found}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Operation
                </Typography>
                <Typography variant="body1">
                  {progress.operation}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Feature Test Cards */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Individual Feature Tests
      </Typography>
      <Grid container spacing={3}>
        {features.map((feature) => {
          const testResult = featureTests.get(feature.key);
          const isRunning = testResult?.status === 'running';
          const progress = testResult
            ? (testResult.tested / testResult.total) * 100
            : 0;

          return (
            <Grid item xs={12} md={6} key={feature.key}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">{feature.name}</Typography>
                    <Tooltip title={feature.description}>
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {feature.description}
                  </Typography>

                  {testResult && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          color={
                            testResult.status === 'error'
                              ? 'error'
                              : testResult.status === 'success'
                              ? 'success'
                              : 'primary'
                          }
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {testResult.tested} / {testResult.total} tested
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {progress.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Chip
                            label={`Passed: ${testResult.passed}`}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Chip
                            label={`Failed: ${testResult.failed}`}
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>

                      {testResult.error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {testResult.error}
                        </Alert>
                      )}
                    </>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => runFeatureTest(feature.key, feature.endpoint)}
                      disabled={isRunning || !!activeTaskId}
                      fullWidth
                    >
                      {isRunning ? 'Testing...' : 'Run Test'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Test Results Table */}
      {featureTests.size > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Test Results Summary
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Tested</TableCell>
                  <TableCell align="right">Passed</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(featureTests.values()).map((result) => {
                  const successRate =
                    result.tested > 0
                      ? ((result.passed / result.tested) * 100).toFixed(1)
                      : '0.0';

                  return (
                    <TableRow key={result.feature}>
                      <TableCell>{result.feature}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={result.status}
                          color={
                            result.status === 'error'
                              ? 'error'
                              : result.status === 'success'
                              ? 'success'
                              : result.status === 'running'
                              ? 'primary'
                              : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{result.tested}</TableCell>
                      <TableCell align="right">{result.passed}</TableCell>
                      <TableCell align="right">{result.failed}</TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={
                            Number(successRate) >= 80
                              ? 'success.main'
                              : Number(successRate) >= 50
                              ? 'warning.main'
                              : 'error.main'
                          }
                        >
                          {successRate}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default Backtest;
